import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { styles } from '../../styles/styles.js';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine, Cell,
} from 'recharts';
import {
  getPerformance, getPerformanceByLeague, getPerformanceByBetType,
  getPerformanceByStrategy, getCalibration, getBankrollHistory,
} from '../../services/tradingApi';

const PERIOD_OPTIONS = [
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: '', label: 'All' },
];

const StatCard = ({ label, value, color }) => (
  <Card style={{ ...styles.cardbutton_solo, marginBottom: '10px', flex: '1 1 150px' }}>
    <Card.Body style={{ padding: '10px' }}>
      <div style={{ color: '#888', fontSize: '11px', marginBottom: '2px' }}>{label}</div>
      <div style={{ color: color || 'white', fontSize: '20px', fontWeight: 'bold' }}>{value}</div>
    </Card.Body>
  </Card>
);

const Performance = () => {
  const [period, setPeriod] = useState('30');
  const [metrics, setMetrics] = useState(null);
  const [bankrollData, setBankrollData] = useState([]);
  const [calibrationData, setCalibrationData] = useState([]);
  const [breakdownTab, setBreakdownTab] = useState('league');
  const [breakdownData, setBreakdownData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = period ? { days: period } : {};
        const [perfRes, bankrollRes, calRes] = await Promise.allSettled([
          getPerformance(params),
          getBankrollHistory({ limit: 500 }),
          getCalibration(),
        ]);

        if (perfRes.status === 'fulfilled') setMetrics(perfRes.value.data);
        if (bankrollRes.status === 'fulfilled') {
          setBankrollData(bankrollRes.value.data.map(e => ({
            date: new Date(e.created_at).toLocaleDateString(),
            balance: parseFloat(e.balance_after),
          })));
        }
        if (calRes.status === 'fulfilled') setCalibrationData(calRes.value.data);
      } catch (err) {
        console.error('Performance fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [period]);

  useEffect(() => {
    const fetch = async () => {
      try {
        let res;
        if (breakdownTab === 'league') res = await getPerformanceByLeague();
        else if (breakdownTab === 'bet_type') res = await getPerformanceByBetType();
        else res = await getPerformanceByStrategy();
        setBreakdownData(res.data);
      } catch (err) {
        console.error('Breakdown fetch error:', err);
      }
    };
    fetch();
  }, [breakdownTab]);

  if (loading) return <div style={{ color: 'white', padding: '2rem' }}>Loading...</div>;

  const pnlColor = (metrics?.total_pnl ?? 0) >= 0 ? '#4caf50' : '#f44336';

  // Build daily P&L from bankroll entries
  const dailyPnl = [];
  const byDate = {};
  bankrollData.forEach((entry, i) => {
    if (i === 0) return;
    const date = entry.date;
    const change = entry.balance - bankrollData[i - 1].balance;
    byDate[date] = (byDate[date] || 0) + change;
  });
  Object.entries(byDate).forEach(([date, pnl]) => {
    dailyPnl.push({ date, pnl: parseFloat(pnl.toFixed(2)) });
  });

  const labelKey = breakdownTab === 'league' ? 'league'
    : breakdownTab === 'bet_type' ? 'bet_type'
    : 'strategy_name';

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px' }}>
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Performance</h2>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
        {PERIOD_OPTIONS.map(o => (
          <Button
            key={o.value}
            size="sm"
            variant={period === o.value ? 'light' : 'outline-secondary'}
            onClick={() => setPeriod(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </div>

      {/* Stat cards */}
      {metrics && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          <StatCard label="Total P&L" value={`$${metrics.total_pnl?.toFixed(2)}`} color={pnlColor} />
          <StatCard label="ROI" value={`${metrics.roi}%`} color={pnlColor} />
          <StatCard label="Win Rate" value={`${metrics.win_rate}%`} />
          <StatCard label="Record" value={`${metrics.wins}W-${metrics.losses}L`} />
          <StatCard label="Profit Factor" value={metrics.profit_factor ?? '--'} />
          <StatCard label="Max Drawdown" value={`$${metrics.max_drawdown}`} color="#f44336" />
        </div>
      )}

      {/* Bankroll chart */}
      {bankrollData.length > 1 && (
        <Card style={{ ...styles.cardbutton_solo, marginBottom: '20px' }}>
          <Card.Body>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Bankroll Over Time</div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={bankrollData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #444' }} />
                <Line type="monotone" dataKey="balance" stroke="#20B2AA" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      )}

      {/* Daily P&L chart */}
      {dailyPnl.length > 0 && (
        <Card style={{ ...styles.cardbutton_solo, marginBottom: '20px' }}>
          <Card.Body>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Daily P&L</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyPnl}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #444' }} />
                <ReferenceLine y={0} stroke="#555" />
                <Bar dataKey="pnl">
                  {dailyPnl.map((entry, i) => (
                    <Cell key={i} fill={entry.pnl >= 0 ? '#4caf50' : '#f44336'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      )}

      {/* Calibration chart */}
      {calibrationData.length > 0 && (
        <Card style={{ ...styles.cardbutton_solo, marginBottom: '20px' }}>
          <Card.Body>
            <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Model Calibration (Expected vs Actual Win Rate)</div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={calibrationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="avg_model_probability" tick={{ fill: '#888', fontSize: 11 }} label={{ value: 'Model Prob %', fill: '#888', fontSize: 11, position: 'insideBottom', offset: -5 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} label={{ value: 'Actual Win %', fill: '#888', fontSize: 11, angle: -90, position: 'insideLeft' }} />
                <Tooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #444' }} />
                <ReferenceLine stroke="#555" strokeDasharray="5 5" segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]} />
                <Line type="monotone" dataKey="actual_win_rate" stroke="#ff9800" dot={{ r: 4 }} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      )}

      {/* Breakdown tables */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '20px' }}>
        <Card.Body>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            {['league', 'bet_type', 'strategy_name'].map(tab => (
              <Button
                key={tab}
                size="sm"
                variant={breakdownTab === tab ? 'light' : 'outline-secondary'}
                onClick={() => setBreakdownTab(tab)}
              >
                {tab === 'league' ? 'By League' : tab === 'bet_type' ? 'By Bet Type' : 'By Strategy'}
              </Button>
            ))}
          </div>

          {breakdownData.length === 0 ? (
            <div style={{ color: '#888' }}>No data</div>
          ) : (
            <Table bordered size="sm" style={{ backgroundColor: 'black', color: 'white', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th>{labelKey === 'league' ? 'League' : labelKey === 'bet_type' ? 'Bet Type' : 'Strategy'}</th>
                  <th>P&L</th>
                  <th>ROI</th>
                  <th>Win Rate</th>
                  <th>Bets</th>
                  <th>Record</th>
                </tr>
              </thead>
              <tbody>
                {breakdownData.map((row, i) => (
                  <tr key={i}>
                    <td>{row[labelKey] || '--'}</td>
                    <td style={{ color: row.total_pnl >= 0 ? '#4caf50' : '#f44336' }}>${row.total_pnl?.toFixed(2)}</td>
                    <td>{row.roi}%</td>
                    <td>{row.win_rate}%</td>
                    <td>{row.total_bets}</td>
                    <td>{row.wins}W-{row.losses}L</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Performance;
